// Configuration for all available search actions in the Edyt platform

import {
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  InboxStackIcon,
  LightBulbIcon,
  ArrowRightCircleIcon,
  MoonIcon,
} from '@heroicons/react/24/outline';

export type SearchActionCategory = 'questions' | 'generation' | 'navigation' | 'utilities';

export interface SearchAction {
  id: string;
  name: string;
  description: string;
  detailedDescription: string;
  category: SearchActionCategory;
  icon: any; // React component for icon
  examples: string[];
  keywords: string[];
  requiresCustomer?: boolean;
}

export const searchActions: SearchAction[] = [
  // QUESTIONS CATEGORY
  {
    id: 'rag_query',
    name: 'Ask Questions',
    description: 'Get insights about account status, health, or recent activity',
    detailedDescription: 'Query account-specific data to understand their current status, health metrics, recent communications, and any issues or risks. Use this for exploratory questions about what\'s happening with an account.',
    category: 'questions',
    icon: ChatBubbleLeftRightIcon,
    examples: [
      'How is Apple doing?',
      'What are the recent issues with Acme Corp?',
      'Tell me about Microsoft\'s health status',
      'Show me latest risks for Globex',
      'What\'s happening with our top accounts?',
    ],
    keywords: ['how is', 'what is', 'tell me about', 'show me', 'status of', 'latest for', 'risks with'],
    requiresCustomer: false,
  },
  
  // GENERATION CATEGORY
  {
    id: 'draft_email',
    name: 'Draft Email',
    description: 'Generate professional account emails',
    detailedDescription: 'Create personalized, professional email drafts for accounts based on recent interactions and context. Supports update, alignment, follow-up, and announcement email types.',
    category: 'generation',
    icon: EnvelopeIcon,
    examples: [
      'Draft email for Apple',
      'Write follow-up email to Acme Corp',
      'Create alignment email for Microsoft',
      'Generate update email for Globex',
      'Compose announcement email for accounts',
    ],
    keywords: ['draft email', 'write email', 'create email', 'generate email', 'compose email', 'email for'],
    requiresCustomer: true,
  },
  {
    id: 'qbr_generation',
    name: 'Generate QBR Report',
    description: 'Create Quarterly Business Review reports',
    detailedDescription: 'Generate comprehensive Quarterly Business Review reports for accounts, including executive summaries, performance reviews, health metrics, and strategic recommendations. Can be exported to PDF using custom templates.',
    category: 'generation',
    icon: DocumentTextIcon,
    examples: [
      'Generate QBR for Apple',
      'Create Q2 report for Microsoft',
      'QBR for Acme Corp for last quarter',
      'Build quarterly review for Globex',
      'Make business review for Apple Q1 2026',
    ],
    keywords: ['generate qbr', 'create qbr', 'qbr for', 'quarterly review', 'business review', 'quarterly report'],
    requiresCustomer: true,
  },
  {
    id: 'briefing_generation',
    name: 'Create Pre-Call Briefing',
    description: 'Generate briefing documents for account meetings',
    detailedDescription: 'Create a pre-call briefing document summarizing recent activity, key topics, and important context to prepare for account meetings. Includes what\'s new since your last interaction.',
    category: 'generation',
    icon: ClipboardDocumentListIcon,
    examples: [
      'Briefing for Apple',
      'Prep for call with Acme Corp',
      'Generate briefing for Microsoft',
      'What\'s new with Globex?',
      'Prepare for meeting with Apple',
    ],
    keywords: ['briefing', 'prep for call', 'prepare for', 'what\'s new with', 'pre-call', 'meeting prep'],
    requiresCustomer: true,
  },
  {
    id: 'weekly_digest',
    name: 'Weekly Portfolio Digest',
    description: 'Generate weekly summary across all accounts',
    detailedDescription: 'Create a comprehensive portfolio-level digest summarizing activity, risks, and opportunities across all accounts. Ideal for leadership updates and weekly team reviews.',
    category: 'generation',
    icon: InboxStackIcon,
    examples: [
      'Generate weekly digest',
      'Create weekly summary',
      'Weekly portfolio update',
      'Leadership digest',
      'Portfolio summary for this week',
    ],
    keywords: ['weekly digest', 'generate digest', 'portfolio summary', 'leadership digest', 'weekly summary'],
    requiresCustomer: false,
  },
  {
    id: 'progress_summary',
    name: 'Track Internal Progress',
    description: 'Monitor progress on internal projects and initiatives',
    detailedDescription: 'Track progress on internal team projects, initiatives, or cross-functional work by searching across all communications and channels. This is for internal project tracking, not account-specific queries.',
    category: 'generation',
    icon: ChartBarIcon,
    examples: [
      'Progress on account onboarding',
      'Status of migration project',
      'Track progress on feature X',
      'Update on quantum computing integration',
      'Team progress on new dashboard',
    ],
    keywords: ['progress on', 'status of', 'track progress', 'update on', 'team progress'],
    requiresCustomer: false,
  },
  
  // UTILITIES CATEGORY
  {
    id: 'advisor_query',
    name: 'Get Strategic Advice',
    description: 'Request expert recommendations and best practices',
    detailedDescription: 'Get strategic advice combining your local account data with industry best practices and web research. Provides PhD-level analysis, recommendations, and actionable strategies.',
    category: 'utilities',
    icon: LightBulbIcon,
    examples: [
      'Advise me on churn prevention',
      'What should I do about low engagement?',
      'Best practices for account onboarding',
      'Recommend strategy for renewal',
      'Expert advice on expansion opportunities',
    ],
    keywords: ['advise', 'recommend', 'best practices', 'should I', 'expert advice', 'strategy for'],
    requiresCustomer: false,
  },
  {
    id: 'ui_navigation',
    name: 'Navigate Pages',
    description: 'Go to specific pages with optional filters',
    detailedDescription: 'Navigate directly to any page in the application. You can also apply filters like "show accounts at risk" or "show churned accounts" to immediately see filtered views.',
    category: 'navigation',
    icon: ArrowRightCircleIcon,
    examples: [
      'Go to accounts page',
      'Show accounts at risk',
      'Take me to analytics',
      'Show churned accounts',
      'Open integrations',
    ],
    keywords: ['go to', 'show', 'take me to', 'open', 'navigate to', 'display'],
    requiresCustomer: false,
  },
  {
    id: 'theme_toggle',
    name: 'Toggle Theme',
    description: 'Switch between dark and light mode',
    detailedDescription: 'Enable or disable dark mode, or toggle between light and dark themes for better viewing comfort.',
    category: 'utilities',
    icon: MoonIcon,
    examples: [
      'Enable dark mode',
      'Turn on dark mode',
      'Switch to light mode',
      'Toggle theme',
      'Change to dark theme',
    ],
    keywords: ['dark mode', 'light mode', 'toggle theme', 'switch theme', 'enable dark', 'disable dark'],
    requiresCustomer: false,
  },
];

// Featured actions for quick suggestions dropdown (most commonly used)
export const featuredActions: string[] = [
  'rag_query',
  'draft_email',
  'qbr_generation',
  'briefing_generation',
  'advisor_query',
  'ui_navigation',
];

// Category labels for grouping
export const categoryLabels: Record<SearchActionCategory, string> = {
  questions: 'Ask Questions',
  generation: 'Generate Documents',
  navigation: 'Navigate & View',
  utilities: 'Tools & Settings',
};

// Helper function to get action by id
export function getActionById(id: string): SearchAction | undefined {
  return searchActions.find(action => action.id === id);
}

// Helper function to get actions by category
export function getActionsByCategory(category: SearchActionCategory): SearchAction[] {
  return searchActions.filter(action => action.category === category);
}
